import React, { useEffect, useState } from "react"
import { connect } from "react-redux"
import { Link } from "react-router-dom"
import { Row, Col, Spinner, Alert, Card, CardBody, CardTitle, Table, Badge } from "reactstrap"
import Miniwidget from "./Miniwidget"
import RecentActivity from "./recent-activity"
import PropertiesInBidding from "./PropertiesInBidding"
import LatestOrders from "./latest-orders"
import ClosedBiddingTable from "./ClosedBiddingTable"
import { setBreadcrumbItems } from "store/actions"
import apiClient from "helpers/apiClient"
import { AUTH_USER_UPDATED_EVENT } from "helpers/auth_api"
import type { PropertyInBidding } from "./PropertiesInBidding"

interface BreadcrumbItem {
  title: string
  link: string
}

interface DashboardProps {
  setBreadcrumbItems: (title: string, items: BreadcrumbItem[]) => void
}

export interface DashboardKpi {
  title: string
  iconClass: string
  total: string
  average: string
  badgecolor: string
  link?: string
}

export interface DashboardActivity {
  date: string
  text: string
}

export interface DashboardTransaction {
  name: string
  propertyTitle?: string
  status: string
  amount: string
  date: string
  color: string
}

export interface DashboardOrder {
  id: string
  name: string
  status: string
  amount: string
  date: string
  color: string
  listingId?: string | null
}

export interface BrokerSellerStat {
  userId: string
  fullName: string
  email: string
  listingCount: number
}

export interface BuyerStat {
  userId: string
  fullName: string
  email: string
  bidCount: number
  leadingCount: number
}

export interface BidderStat {
  userId: string
  fullName: string
  email: string
  role: string
  bidCount: number
  leadingCount: number
}


export interface DashboardData {
  role?: string
  kpis: DashboardKpi[]
  recentActivity: (DashboardActivity & { listingId?: string | null; userId?: string | null; bidId?: string | null })[]
  latestTransactions: (DashboardTransaction & { listingId?: string | null; userId?: string | null })[]
  latestOrders: DashboardOrder[]
  brokerStats?: BrokerSellerStat[]
  sellerStats?: BrokerSellerStat[]
  buyerStats?: BuyerStat[]
  adminBidStats?: BidderStat[]
  brokerBidStats?: BidderStat[]
  sellerBidStats?: BidderStat[]
  totalActivityCount?: number
  activityPage?: number
  activityLimit?: number
  propertiesInBidding?: PropertyInBidding[]
}

const DEFAULT_KPIS: DashboardKpi[] = [
  { title: "Listings", iconClass: "cube-outline", total: "—", average: "—", badgecolor: "info" },
  { title: "Bids", iconClass: "buffer", total: "—", average: "—", badgecolor: "info" },
  { title: "—", iconClass: "tag-text-outline", total: "—", average: "—", badgecolor: "secondary" },
  { title: "—", iconClass: "briefcase-check", total: "—", average: "—", badgecolor: "secondary" },
]

const DASHBOARD_BREADCRUMB: BreadcrumbItem[] = [
  { title: "SOLDX", link: "#" },
  { title: "Dashboard", link: "#" },
]

/** Deep link for each KPI card by role + API title */
function kpiLinkForDashboard(
  normalizedRole: string,
  isAdminRole: boolean,
  title: string,
  index: number,
): string | undefined {
  const t = title.trim().toLowerCase()
  if (isAdminRole) {
    if (t.includes("listing") && !t.includes("live")) return "/listings"
    if (t.includes("bid")) return "/bidding-overview"
    if (t.includes("user")) return "/users"
    if (t.includes("live") && t.includes("auction")) return "/live-properties"
    return ["/listings", "/bidding-overview", "/users", "/live-properties"][index]
  }
  if (normalizedRole === "buyer") {
    return "/my-bids"
  }
  if (normalizedRole === "broker" || normalizedRole === "seller") {
    if (t === "my listings" || (t.includes("listing") && t.includes("my"))) return "/listings"
    if (t.includes("bids on my")) return "/closed-bidding"
    if (t.includes("live") || t.includes("upcoming")) return "/live-properties"
    if (t === "properties") return "/properties"
    return ["/listings", "/closed-bidding", "/live-properties", "/properties"][index]
  }
  return undefined
}

const Dashboard = (props: DashboardProps) => {
  document.title = "Dashboard | SOLDX"

  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activityPage, setActivityPage] = useState(1)
  const [authSyncKey, setAuthSyncKey] = useState(0)
  const ACTIVITY_LIMIT = 10

  useEffect(() => {
    props.setBreadcrumbItems("Dashboard", DASHBOARD_BREADCRUMB)
  }, [props])

  useEffect(() => {
    const onAuthUpdated = () => setAuthSyncKey((k) => k + 1)
    window.addEventListener(AUTH_USER_UPDATED_EVENT, onAuthUpdated)
    return () => window.removeEventListener(AUTH_USER_UPDATED_EVENT, onAuthUpdated)
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const params = new URLSearchParams()
    params.set("activityPage", String(activityPage))
    params.set("activityLimit", String(ACTIVITY_LIMIT))
    apiClient
      .get<{ success: boolean; data?: DashboardData }>(`/api/dashboard?${params.toString()}`)
      .then((res) => {
        if (!cancelled && res.data?.success && res.data.data) {
          setData(res.data.data)
          setError(null)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = err?.response?.data?.message || err?.message || "Failed to load dashboard."
          setError(msg)
          setData(null)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [activityPage, authSyncKey])

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5">
        <Spinner color="primary" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert color="warning" className="mb-0">
        <strong>Dashboard</strong> — {error}
        {error.toLowerCase().includes("login") && (
          <span className="ms-2">
            <a href="/login">Login</a> to see role-based stats (Super-admin, Admin, Buyer, Broker, Seller).
          </span>
        )}
      </Alert>
    )
  }

  const rawKpis = data?.kpis?.length ? data.kpis : DEFAULT_KPIS
  // Only Super-admin / Admin see admin sections (Broker/Seller/Buyer stats, listing/bidding links). Buyer/Seller/Broker see only role-specific KPIs and content.
  const isAdminRole = Boolean(
    data?.role && /super-admin|super\s*admin|^admin$/i.test(String(data.role).trim())
  )
  const normalizedRole = data?.role ? String(data.role).trim().toLowerCase().replace(/\s+/g, "-") : ""
  const canHaveListings = isAdminRole || normalizedRole === "broker" || normalizedRole === "seller"
  const kpis = rawKpis.map((k, i) => ({
    ...k,
    link: kpiLinkForDashboard(normalizedRole, isAdminRole, k.title, i),
  }))
  const recentActivity = data?.recentActivity ?? []
  const latestOrders = data?.latestOrders ?? []
  const propertiesInBidding = data?.propertiesInBidding ?? []
  const brokerStats = data?.brokerStats ?? []
  const sellerStats = data?.sellerStats ?? []
  const buyerStats = data?.buyerStats ?? []
  const adminBidStats = data?.adminBidStats ?? []
  const brokerBidStats = data?.brokerBidStats ?? []
  const sellerBidStats = data?.sellerBidStats ?? []

  return (
    <React.Fragment>
      {data?.role && (
        <p className="text-muted mb-3">
          Viewing as: <strong>{data.role}</strong>
          {isAdminRole && (
            <>
              {" · "}
              <Link to="/bidding-overview">Bidding Overview</Link>
              {" · "}
              <Link to="/listings">All Listings</Link>
            </>
          )}
        </p>
      )}
      <Miniwidget reports={kpis} />
      {isAdminRole && (brokerStats.length > 0 || sellerStats.length > 0 || buyerStats.length > 0) && (
        <Row className="mt-3">
          <Col xl="4" lg="4" md="6" sm="12" className="mb-3 mb-xl-0">
            <Card className="h-100">
              <CardBody>
                <CardTitle tag="h5" className="mb-3">Brokers – Listings</CardTitle>
                <p className="text-muted small">Number of properties listed per broker</p>
                <div className="table-responsive">
                  <Table size="sm" className="mb-0">
                    <thead><tr><th>Name</th><th>Email</th><th className="text-end">Listings</th><th></th></tr></thead>
                    <tbody>
                      {brokerStats.slice(0, 5).map((b) => (
                        <tr key={b.userId}>
                          <td>{b.fullName}</td>
                          <td><span className="text-muted text-break">{b.email}</span></td>
                          <td className="text-end"><Badge color="primary">{b.listingCount}</Badge></td>
                          <td>
                            <Link to={`/listings?createdBy=${b.userId}`} className="btn btn-sm btn-outline-primary" title="View"><i className="mdi mdi-eye" /></Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                {brokerStats.length > 5 && (
                  <div className="mt-2 text-center">
                    <Link to="/user-stats?type=broker-listings" className="btn btn-sm btn-outline-secondary">
                      View More ({brokerStats.length - 5} more)
                    </Link>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
          <Col xl="4" lg="4" md="6" sm="12" className="mb-3 mb-xl-0">
            <Card className="h-100">
              <CardBody>
                <CardTitle tag="h5" className="mb-3">Sellers – Listings</CardTitle>
                <p className="text-muted small">Number of properties listed per seller</p>
                <div className="table-responsive">
                  <Table size="sm" className="mb-0">
                    <thead><tr><th>Name</th><th>Email</th><th className="text-end">Listings</th><th></th></tr></thead>
                    <tbody>
                      {sellerStats.slice(0, 5).map((s) => (
                        <tr key={s.userId}>
                          <td>{s.fullName}</td>
                          <td><span className="text-muted text-break">{s.email}</span></td>
                          <td className="text-end"><Badge color="success">{s.listingCount}</Badge></td>
                          <td>
                            <Link to={`/listings?createdBy=${s.userId}`} className="btn btn-sm btn-outline-success" title="View"><i className="mdi mdi-eye" /></Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                {sellerStats.length > 5 && (
                  <div className="mt-2 text-center">
                    <Link to="/user-stats?type=seller-listings" className="btn btn-sm btn-outline-secondary">
                      View More ({sellerStats.length - 5} more)
                    </Link>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
          <Col xl="4" lg="4" md="12" sm="12">
            <Card className="h-100">
              <CardBody>
                <CardTitle tag="h5" className="mb-3">Buyers – Bids</CardTitle>
                <p className="text-muted small">Number of bids and leading bids per buyer</p>
                <div className="table-responsive">
                  <Table size="sm" className="mb-0">
                    <thead><tr><th>Name</th><th>Email</th><th className="text-end">Bids</th><th className="text-end">Leading</th><th></th></tr></thead>
                    <tbody>
                      {buyerStats.slice(0, 5).map((b) => (
                        <tr key={b.userId}>
                          <td>{b.fullName}</td>
                          <td><span className="text-muted text-break">{b.email}</span></td>
                          <td className="text-end"><Badge color="info">{b.bidCount}</Badge></td>
                          <td className="text-end"><Badge color="warning">{b.leadingCount}</Badge></td>
                          <td><Link to={`/bidding-overview?userId=${b.userId}`} className="btn btn-sm btn-outline-info" title="View"><i className="mdi mdi-eye" /></Link></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                {buyerStats.length > 5 && (
                  <div className="mt-2 text-center">
                    <Link to="/user-stats?type=buyer-bids" className="btn btn-sm btn-outline-secondary">
                      View More ({buyerStats.length - 5} more)
                    </Link>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
      {/* Admin/Broker/Seller Bids Section */}
      {isAdminRole && (adminBidStats.length > 0 || brokerBidStats.length > 0 || sellerBidStats.length > 0) && (
        <Row className="mt-3">
          <Col xl="4" lg="4" md="6" sm="12" className="mb-3 mb-xl-0">
            <Card className="h-100">
              <CardBody>
                <CardTitle tag="h5" className="mb-3">
                  <i className="mdi mdi-shield-account me-2 text-danger" />
                  Admin – Bids
                </CardTitle>
                <p className="text-muted small">Bids placed by admins</p>
                <div className="table-responsive">
                  <Table size="sm" className="mb-0">
                    <thead><tr><th>Name</th><th>Email</th><th className="text-end">Bids</th><th className="text-end">Leading</th><th></th></tr></thead>
                    <tbody>
                      {adminBidStats.length === 0 ? (
                        <tr><td colSpan={5} className="text-muted text-center">No admin bids</td></tr>
                      ) : (
                        adminBidStats.slice(0, 5).map((b) => (
                          <tr key={b.userId}>
                            <td>{b.fullName}</td>
                            <td><span className="text-muted text-break">{b.email}</span></td>
                            <td className="text-end"><Badge color="danger">{b.bidCount}</Badge></td>
                            <td className="text-end"><Badge color="warning">{b.leadingCount}</Badge></td>
                            <td><Link to={`/bidding-overview?userId=${b.userId}`} className="btn btn-sm btn-outline-danger" title="View"><i className="mdi mdi-eye" /></Link></td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
                {adminBidStats.length > 5 && (
                  <div className="mt-2 text-center">
                    <Link to="/user-stats?type=admin-bids" className="btn btn-sm btn-outline-secondary">
                      View More ({adminBidStats.length - 5} more)
                    </Link>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
          <Col xl="4" lg="4" md="6" sm="12" className="mb-3 mb-xl-0">
            <Card className="h-100">
              <CardBody>
                <CardTitle tag="h5" className="mb-3">
                  <i className="mdi mdi-account-tie me-2 text-primary" />
                  Brokers – Bids
                </CardTitle>
                <p className="text-muted small">Bids placed by brokers</p>
                <div className="table-responsive">
                  <Table size="sm" className="mb-0">
                    <thead><tr><th>Name</th><th>Email</th><th className="text-end">Bids</th><th className="text-end">Leading</th><th></th></tr></thead>
                    <tbody>
                      {brokerBidStats.length === 0 ? (
                        <tr><td colSpan={5} className="text-muted text-center">No broker bids</td></tr>
                      ) : (
                        brokerBidStats.slice(0, 5).map((b) => (
                          <tr key={b.userId}>
                            <td>{b.fullName}</td>
                            <td><span className="text-muted text-break">{b.email}</span></td>
                            <td className="text-end"><Badge color="primary">{b.bidCount}</Badge></td>
                            <td className="text-end"><Badge color="warning">{b.leadingCount}</Badge></td>
                            <td><Link to={`/bidding-overview?userId=${b.userId}`} className="btn btn-sm btn-outline-primary" title="View"><i className="mdi mdi-eye" /></Link></td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
                {brokerBidStats.length > 5 && (
                  <div className="mt-2 text-center">
                    <Link to="/user-stats?type=broker-bids" className="btn btn-sm btn-outline-secondary">
                      View More ({brokerBidStats.length - 5} more)
                    </Link>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
          <Col xl="4" lg="4" md="12" sm="12">
            <Card className="h-100">
              <CardBody>
                <CardTitle tag="h5" className="mb-3">
                  <i className="mdi mdi-account-cash me-2 text-success" />
                  Sellers – Bids
                </CardTitle>
                <p className="text-muted small">Bids placed by sellers</p>
                <div className="table-responsive">
                  <Table size="sm" className="mb-0">
                    <thead><tr><th>Name</th><th>Email</th><th className="text-end">Bids</th><th className="text-end">Leading</th><th></th></tr></thead>
                    <tbody>
                      {sellerBidStats.length === 0 ? (
                        <tr><td colSpan={5} className="text-muted text-center">No seller bids</td></tr>
                      ) : (
                        sellerBidStats.slice(0, 5).map((b) => (
                          <tr key={b.userId}>
                            <td>{b.fullName}</td>
                            <td><span className="text-muted text-break">{b.email}</span></td>
                            <td className="text-end"><Badge color="success">{b.bidCount}</Badge></td>
                            <td className="text-end"><Badge color="warning">{b.leadingCount}</Badge></td>
                            <td><Link to={`/bidding-overview?userId=${b.userId}`} className="btn btn-sm btn-outline-success" title="View"><i className="mdi mdi-eye" /></Link></td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
                {sellerBidStats.length > 5 && (
                  <div className="mt-2 text-center">
                    <Link to="/user-stats?type=seller-bids" className="btn btn-sm btn-outline-secondary">
                      View More ({sellerBidStats.length - 5} more)
                    </Link>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
      <Row className="mt-4">
        <Col xl="6" lg="6" md="12" className="mb-3 mb-xl-0">
          <PropertiesInBidding properties={propertiesInBidding} />
        </Col>
        <Col xl="6" lg="6" md="12">
          <LatestOrders orders={latestOrders} />
        </Col>
      </Row>

      {canHaveListings && (
        <Row>
          <Col xl="12">
            <ClosedBiddingTable />
          </Col>
        </Row>
      )}

      <Row>
        <Col xl="12">
          <RecentActivity
            items={recentActivity}
            totalCount={data?.totalActivityCount ?? 0}
            page={data?.activityPage ?? 1}
            limit={data?.activityLimit ?? ACTIVITY_LIMIT}
            onPageChange={setActivityPage}
          />
        </Col>
      </Row>


   
    </React.Fragment>
  )
}

export default connect(null, { setBreadcrumbItems })(Dashboard)